import { useEffect } from 'react';

const GROUP_LIMIT = 3;
const PEOPLE_LIMIT = 6;

function firstCategories(categories) {
  if (!Array.isArray(categories)) return [];
  return categories
    .filter((category) => typeof category === 'string' && category.trim())
    .slice(0, GROUP_LIMIT);
}

function authHeaders() {
  return {
    Authorization: `Bearer ${localStorage.getItem('token')}`,
    'Content-Type': 'application/json',
  };
}

async function fetchMarketplaceItems(path) {
  const response = await fetch(`${process.env.REACT_APP_API_URL}${path}`, {
    headers: authHeaders(),
  });
  if (!response.ok) throw new Error('Failed to load suggestions');
  const data = await response.json();
  return Array.isArray(data.items) ? data.items : [];
}

async function loadCategoryGroups(categories, loadPeople, describeGroup) {
  const selected = firstCategories(categories);
  const groups = await Promise.all(selected.map(async (category) => {
    let people = [];
    try {
      people = await loadPeople(category);
    } catch {
      people = [];
    }
    return {
      id: category,
      title: category,
      description: describeGroup(category),
      people,
    };
  }));
  return groups.filter((group) => group.people.length > 0);
}

async function withSuggestedFallback(groups, loadSuggested, title, description) {
  if (groups.length > 0) return groups;
  let people = [];
  try {
    people = await loadSuggested();
  } catch {
    people = [];
  }
  if (people.length === 0) return [];
  return [{
    id: 'suggested',
    title,
    description,
    people,
  }];
}

function brandPerson(item) {
  if (!item?.id || !item?.name) return null;
  return {
    id: item.id,
    name: item.name,
    imageUrl: item.brand_logo_url || '',
    subtitle: '',
  };
}

function creatorSubtitle(item) {
  const handle = [item.tiktok_handle, item.instagram_handle, item.whatnot_handle]
    .find((value) => String(value || '').trim());
  if (!handle) return '';
  return `@${String(handle).trim().replace(/^@/, '')}`;
}

function creatorPerson(item) {
  if (!item?.id || !item?.full_name) return null;
  return {
    id: item.id,
    name: item.full_name,
    imageUrl: item.profile_picture_url || '',
    subtitle: creatorSubtitle(item),
  };
}

async function loadBrandPeople(params) {
  const items = await fetchMarketplaceItems(`/api/brand/marketplace?${params}`);
  return items.map(brandPerson).filter(Boolean);
}

async function loadCreatorPeople(params) {
  const items = await fetchMarketplaceItems(`/api/influencer/marketplace?${params}`);
  return items.map(creatorPerson).filter(Boolean);
}

export async function fetchBrandFollowGroups(categories) {
  const groups = await loadCategoryGroups(
    categories,
    async (category) => {
      const params = new URLSearchParams({ industries: category, limit: String(PEOPLE_LIMIT) });
      return loadBrandPeople(params);
    },
    (category) => `Brands posting in ${category}.`,
  );
  return withSuggestedFallback(
    groups,
    () => loadBrandPeople(new URLSearchParams({ limit: String(PEOPLE_LIMIT) })),
    'Suggested brands',
    'Brands ready to work with creators.',
  );
}

export async function fetchCreatorSaveGroups(categories) {
  const groups = await loadCategoryGroups(
    categories,
    async (category) => {
      const params = new URLSearchParams({ categories: category, limit: String(PEOPLE_LIMIT) });
      return loadCreatorPeople(params);
    },
    (category) => `Creators in ${category}.`,
  );
  return withSuggestedFallback(
    groups,
    () => loadCreatorPeople(new URLSearchParams({ limit: String(PEOPLE_LIMIT) })),
    'Suggested creators',
    'Creators ready to work with brands.',
  );
}

export function followSuggestionHelperText(formData, defaultHelper, emptyHelper) {
  if (formData?.followSuggestionsStatus !== 'ready') return defaultHelper;
  const groups = Array.isArray(formData.followGroups) ? formData.followGroups : [];
  const hasPeople = groups.some((group) => Array.isArray(group.people) && group.people.length > 0);
  if (!hasPeople) return emptyHelper;
  return defaultHelper;
}

export function validateFollowSuggestions(formData = {}) {
  if (formData.followSuggestionsStatus !== 'ready') return null;
  const groups = Array.isArray(formData.followGroups) ? formData.followGroups : [];
  const hasPeople = groups.some((group) => Array.isArray(group.people) && group.people.length > 0);
  if (!hasPeople) return null;
  const selected = Array.isArray(formData.selectedFollowIds) ? formData.selectedFollowIds : [];
  if (selected.length === 0) return 'Select 1 or more to continue';
  return null;
}

export function isFollowSelectionReady(formData) {
  if (formData?.followSuggestionsStatus !== 'ready') return false;
  return validateFollowSuggestions(formData) === null;
}

export function useSyncFollowSuggestions({
  active,
  categories,
  loadGroups,
  onLoaded,
}) {
  useEffect(() => {
    if (!active) return undefined;
    let cancelled = false;
    onLoaded({ status: 'loading', groups: [] });
    loadGroups(categories).then((groups) => {
      if (cancelled) return;
      onLoaded({ status: 'ready', groups });
    });
    return () => {
      cancelled = true;
    };
  }, [active, categories, loadGroups, onLoaded]);
}
